import * as React from 'react';
import { ProgressBar } from 'react-bootstrap';
import Dropzone from 'dropzone';
import { DropzoneFile, DropzoneOptions } from 'dropzone';
import notify from '../../../shared/feedback/notifications';
import { isTranslationOf, Translate } from '../../../shared/LanguageService';
import Api from '../../../shared/Api';
import PhotographerUploadsApi from '../api/PhotographerUploadsApi';
import { getUserManager } from '../../../shared/userManager';

interface IProps {
    orderId: number;
    orderRowId: number;
    upload: boolean;
    toggleDragging: (dragging: boolean) => void;
    updateTotalBytes: (bytes: number) => void;
    updateProgress: (bytes: number, filename: string, orderRowId: number, category: any) => void;
    onUploadDone: (anyUploads: boolean) => void;
    setOrderRowIdUploadDone: (orderRowId: number) => void;
    addFileToMetadataList: (orderRowId: number, category: number, filename: string) => void;
    removeFileFromMetadataList: (orderRowId: number, category: number, filename: string) => void;
    updateFilesAdded?: (filesAdded: number) => void;
    canAddFile?: (filename: string) => boolean;
    resetAfterComplete?: boolean;
    isEdited?: boolean;
    hideNormalUploadBox?: boolean;
    hideExtraUploadBox?: boolean;
    hideNoDeliveryUploadBox?: boolean;
    customExtraUploadBoxTitle?: string;
    setOrderRowIdAllFilesProcessed?: (orderRowId: number) => void;
    isPhotographerUploadPage: boolean;
    uploadDNG?: boolean;
}

interface IState {
    numberOfNormalFiles: number;
    numberOfExtraFiles: number;
    numberOfNoDeliveryFiles: number;
    filesAdded: number;
    totalBytes: number;
    uploadProgress: number;
    normalUploadDone: boolean;
    extraUploadDone: boolean;
    noDeliveryUploadDone: boolean;
    uploading: boolean;
    uploadDone: boolean;
    progressObject: object;
    uploadError: object;
    thumbnailTracking: any;
    fileDragState: any;
    fileArray: any[];
    numberOfNormalFilesProcessed: number;
    numberOfExtraFilesProcessed: number;
    numberOfNoDeliveryFilesProcessed: number;
}

class PhotographerUploadBoxes extends React.Component<IProps, IState> {
    private _normalDropzone: Dropzone;
    private _extraDropzone: Dropzone;
    private _noDeliveryDropzone: Dropzone;

    constructor(props: IProps) {
        super(props);

        // @ts-ignore
        Dropzone.autoDiscover = false;

        this.state = {
            uploading: false,
            uploadDone: false,
            normalUploadDone: false,
            extraUploadDone: false,
            noDeliveryUploadDone: false,
            totalBytes: 0,
            progressObject: {},
            uploadProgress: 0,
            uploadError: {},
            filesAdded: 0,
            thumbnailTracking: {
                filesAdded: 0,
                thumbnailsLoaded: 0,
                category: 0
            },
            fileDragState: {
                moving: false,
                file: null,
                category: 0
            },
            fileArray: [],
            numberOfNormalFiles: 0,
            numberOfExtraFiles: 0,
            numberOfNoDeliveryFiles: 0,
            numberOfNormalFilesProcessed: 0,
            numberOfExtraFilesProcessed: 0,
            numberOfNoDeliveryFilesProcessed: 0
        };

        this.onDrop = this.onDrop.bind(this);
        this.checkIfAllFilesProcessed = this.checkIfAllFilesProcessed.bind(this);
    }

    componentDidMount() {
        if (this.props.hideNormalUploadBox !== true) {
            this._normalDropzone = new Dropzone(
                `#normal${this.props.orderRowId}`,
                this.getOptions(1)
            );
        }

        if (this.props.hideExtraUploadBox !== true) {
            this._extraDropzone = new Dropzone(
                `#extra${this.props.orderRowId}`,
                this.getOptions(2)
            );
        }

        if (this.props.hideNoDeliveryUploadBox !== true) {
            this._noDeliveryDropzone = new Dropzone(
                `#nodelivery${this.props.orderRowId}`,
                this.getOptions(3)
            );
        }

        window.addEventListener('dragover', this.onDragover, false);
        window.addEventListener('drop', this.onDrop, false);
        window.addEventListener('online', () => {
            if (this._normalDropzone) {
                this._normalDropzone.processQueue();
            }
            if (this._extraDropzone) {
                this._extraDropzone.processQueue();
            }
            if (this._noDeliveryDropzone) {
                this._noDeliveryDropzone.processQueue();
            }
        });
    }

    componentDidUpdate(prevProps) {
        let { upload } = this.props;

        if (upload) {
            if (
                (!this._normalDropzone || this._normalDropzone.files.length === 0) &&
                (!this._extraDropzone || this._extraDropzone.files.length === 0) &&
                (!this._noDeliveryDropzone || this._noDeliveryDropzone.files.length === 0)
            ) {
                this.props.onUploadDone(false);
            } else if (!this.state.uploading && !this.state.uploadDone) {
                if (this._normalDropzone && this._normalDropzone.getQueuedFiles().length > 0) {
                    this._normalDropzone.processQueue();
                } else if (this._extraDropzone && this._extraDropzone.getQueuedFiles().length > 0) {
                    this._extraDropzone.processQueue();
                } else if (
                    this._noDeliveryDropzone &&
                    this._noDeliveryDropzone.getQueuedFiles().length > 0
                ) {
                    this._noDeliveryDropzone.processQueue();
                } else {
                    this.props.onUploadDone(false);
                }

                this.setState({ uploading: true, uploadProgress: 0 } as IState);
            }
        }
    }

    componentWillUnmount() {
        window.removeEventListener('dragover', this.onDragover);
        window.removeEventListener('drop', this.onDrop);

        if (this._normalDropzone) {
            this.destroyDropzone(this._normalDropzone);
        }

        if (this._extraDropzone) {
            this.destroyDropzone(this._extraDropzone);
        }

        if (this._noDeliveryDropzone) {
            this.destroyDropzone(this._noDeliveryDropzone);
        }
    }

    checkIfAllFilesProcessed(): boolean {
        const {
            numberOfNormalFilesProcessed,
            numberOfExtraFilesProcessed,
            numberOfNoDeliveryFilesProcessed,
            numberOfNormalFiles,
            numberOfExtraFiles,
            numberOfNoDeliveryFiles
        } = this.state;

        return (
            numberOfNormalFilesProcessed === numberOfNormalFiles &&
            numberOfExtraFilesProcessed === numberOfExtraFiles &&
            numberOfNoDeliveryFilesProcessed === numberOfNoDeliveryFiles
        );
    }

    destroyDropzone(dropzone: Dropzone) {
        dropzone.off('addedfile');
        dropzone.off('thumbnail');
        dropzone.off('drop');
        dropzone.off('dragleave');
        dropzone.off('queuecomplete');
        dropzone.off('removedfile');
        dropzone.off('uploadprogress');
        dropzone.off('success');
        dropzone.off('processing');
        dropzone.off('error');
        dropzone.off('canceled');
        dropzone.off('sending');
        dropzone.destroy();
        dropzone = null;
    }

    onDragover(e: any) {
        e.preventDefault();
    }

    onDrop(e: DragEvent) {
        e.preventDefault();
        this.removeFileFromFileDragState();
    }

    getOptions(category: number) {
        let acceptedFiles = 'image/jpeg,image/png,image/gif,application/pdf';
        if (this.props.uploadDNG) acceptedFiles += '.dng,image/x-adobe-dng,image/DNG,image/dng'; 

        let component = this;
        let options: DropzoneOptions = {
            acceptedFiles: acceptedFiles,
            dictDefaultMessage: Translate('dropzone/title'),
            dictFallbackMessage: Translate('dropzone/fallback'),
            dictInvalidFileType: Translate('dropzone/invalid-filetype'),
            dictCancelUpload: Translate('dropzone/cancel-upload'),
            dictCancelUploadConfirmation: Translate('dropzone/cancel-upload-confirmation'),
            autoProcessQueue: false,
            addRemoveLinks: false,
            uploadMultiple: false,
            parallelUploads: 2,
            timeout: 600000,
            parallelChunkUploads: true,
            retryChunks: true,
            retryChunksLimit: 20, // Number of retry attempts per chunk
            forceChunking: true,
            chunking: true,
            chunkSize: 4 * 1024 * 1024,
            previewTemplate: listPhotoTemplate,
            createImageThumbnails: false,
            headers: new Api().appendHeaders(),
            init: function () {
                this.on('addedfile', (file: DropzoneFile) => {
                    if (file) {
                        if (
                            this.options.acceptedFiles &&
                            !this.options.acceptedFiles.includes(file.type)
                        ) {
                            notify.failure(`${file.name} ${Translate('dropzone/invalid-filetype')}`);
                            component.setState({
                                progressObject: {}
                            } as IState);
                            this.removeFile(file);
                            return;
                        }

                        let filesAdded = component.state.filesAdded;
                        let fileArray = component.state.fileArray;
                        let fileDragState = component.state.fileDragState;
                        let numberOfNormalFiles = component.state.numberOfNormalFiles;
                        let numberOfExtraFiles = component.state.numberOfExtraFiles;
                        let numberOfNoDeliveryFiles = component.state.numberOfNoDeliveryFiles;
                        let numberOfNormalFilesProcessed =
                            component.state.numberOfNormalFilesProcessed;
                        let numberOfExtraFilesProcessed =
                            component.state.numberOfExtraFilesProcessed;
                        let numberOfNoDeliveryFilesProcessed =
                            component.state.numberOfNoDeliveryFilesProcessed;

                        let canAddFile = true;

                        if (component.props.canAddFile) {
                            canAddFile = component.props.canAddFile(file.name);
                        }

                        if (
                            canAddFile &&
                            fileArray.findIndex(
                                (addedFile: DropzoneFile) => addedFile.name === file.name
                            ) === -1
                        ) {
                            component.updateTotalBytes(file.size);

                            fileDragState.file = null;
                            fileDragState.category = 0;

                            switch (category) {
                                case 1:
                                    filesAdded++;
                                    numberOfNormalFiles++;
                                    break;
                                case 2:
                                    filesAdded++;
                                    numberOfExtraFiles++;
                                    break;
                                case 3:
                                    filesAdded++;
                                    numberOfNoDeliveryFiles++;
                                    break;
                                default:
                                    break;
                            }

                            numberOfNormalFilesProcessed = 0;
                            numberOfExtraFilesProcessed = 0;
                            numberOfNoDeliveryFilesProcessed = 0;

                            let thumbnailTracking = component.state.thumbnailTracking;
                            thumbnailTracking.filesAdded++;
                            thumbnailTracking.category = category;

                            fileArray.push(file);
                            component.setState({
                                fileArray: fileArray,
                                filesAdded: filesAdded,
                                fileDragState: fileDragState,
                                numberOfNormalFiles: numberOfNormalFiles,
                                numberOfExtraFiles: numberOfExtraFiles,
                                numberOfNoDeliveryFiles: numberOfNoDeliveryFiles,
                                numberOfNormalFilesProcessed: numberOfNormalFilesProcessed,
                                numberOfExtraFilesProcessed: numberOfExtraFilesProcessed,
                                numberOfNoDeliveryFilesProcessed: numberOfNoDeliveryFilesProcessed
                            } as IState);

                            if (component.props.updateFilesAdded) {
                                component.props.updateFilesAdded(filesAdded);
                                let dropzoneCategory = parseInt(
                                    this.element.attributes['data-category'].value
                                );
                                component.props.addFileToMetadataList(
                                    component.props.orderRowId,
                                    dropzoneCategory,
                                    file.name
                                );
                            }
                        } else {
                            notify.failure(
                                `${file.name} ${Translate(
                                    'photographer-upload/notification/error/has-already-been-added-to-order-row'
                                )}`
                            );
                            file['existError'] = true;
                            this.removeFile(file);
                        }
                    }
                });

                this.on('drop', (e: DragEvent) => {
                    PhotographerUploadsApi.sendWakeUpImageScaleFunction();

                    let fileDragState = component.state.fileDragState;
                    if (component.state.fileDragState.file) {
                        this.addFile(component.state.fileDragState.file);
                        fileDragState.file = null;
                        fileDragState.category = 0;
                    }

                    fileDragState.moving = false;
                    component.props.toggleDragging(false);
                    component.setState({
                        fileDragState: fileDragState
                    } as IState);
                });

                this.on('dragleave', (e: DragEvent) => {
                    let fileArray = component.state.fileArray;
                    let fileDragState = component.state.fileDragState;

                    if (fileDragState.moving === true) {
                        return false;
                    }

                    let fileIndex = -1;
                    fileArray.forEach((file: DropzoneFile, index: number) => {
                        if (
                            (e.srcElement['alt'] && e.srcElement['alt'] === file.name) ||
                            (e.dataTransfer.files[0] && e.dataTransfer.files[0].name === file.name)
                        ) {
                            fileIndex = index;
                        }
                    });

                    if (fileIndex !== -1) {
                        fileDragState.file = fileArray[fileIndex];
                        fileDragState.category = category;
                        this.removeFile(fileArray[fileIndex]);
                        fileArray.splice(fileIndex, 1);
                    }
                    fileDragState.moving = true;
                    component.props.toggleDragging(true);

                    component.setState({
                        fileDragState: fileDragState,
                        fileArray: fileArray
                    } as IState);
                });

                this.on('queuecomplete', () => {
                    if (!component.state.filesAdded) {
                        return;
                    }

                    switch (category) {
                        case 1:
                            component.setState({
                                normalUploadDone: true
                            } as IState);

                            if (component._extraDropzone) {
                                component.setState({
                                    extraUploadDone:
                                        component._extraDropzone.getQueuedFiles().length === 0
                                } as IState);
                            }

                            if (component._noDeliveryDropzone) {
                                component.setState({
                                    noDeliveryUploadDone:
                                        component._noDeliveryDropzone.getQueuedFiles().length === 0
                                } as IState);
                            }

                            if (
                                component._extraDropzone &&
                                component._extraDropzone.getQueuedFiles().length > 0
                            ) {
                                component._extraDropzone.processQueue();
                            } else if (
                                component._noDeliveryDropzone &&
                                component._noDeliveryDropzone.getQueuedFiles().length > 0
                            ) {
                                component._noDeliveryDropzone.processQueue();
                            }

                            break;
                        case 2:
                            component.setState({
                                extraUploadDone: true
                            } as IState);

                            if (component._normalDropzone) {
                                component.setState({
                                    normalUploadDone:
                                        component._normalDropzone.getQueuedFiles().length === 0
                                } as IState);
                            }

                            if (component._noDeliveryDropzone) {
                                component.setState({
                                    noDeliveryUploadDone:
                                        component._noDeliveryDropzone.getQueuedFiles().length === 0
                                } as IState);
                            }

                            if (
                                component._noDeliveryDropzone &&
                                component._noDeliveryDropzone.getQueuedFiles().length > 0
                            ) {
                                component._noDeliveryDropzone.processQueue();
                            }

                            break;
                        case 3:
                            component.setState({
                                noDeliveryUploadDone: true
                            } as IState);

                            if (component._normalDropzone) {
                                component.setState({
                                    normalUploadDone:
                                        component._normalDropzone.getQueuedFiles().length === 0
                                } as IState);
                            }

                            if (component._extraDropzone) {
                                component.setState({
                                    extraUploadDone:
                                        component._extraDropzone.getQueuedFiles().length === 0
                                } as IState);
                            }
                            break;
                        default:
                            break;
                    }

                    this.options.autoProcessQueue = false;

                    if (
                        (!component._normalDropzone || component.state.normalUploadDone) &&
                        (!component._extraDropzone || component.state.extraUploadDone) &&
                        (!component._noDeliveryDropzone || component.state.noDeliveryUploadDone) &&
                        component.state.uploading === true
                    ) {
                        component.props.setOrderRowIdUploadDone(component.props.orderRowId);
                    }
                });

                this.on('removedfile', (file: DropzoneFile) => {
                    if (!file['existError'] || file['existError'] !== true) {
                        let filesAdded = component.state.filesAdded;
                        if (filesAdded) {
                            component.props.removeFileFromMetadataList(
                                component.props.orderRowId,
                                category,
                                file.name
                            );
                            component.props.updateTotalBytes(file.size * -1);
                            component.updateTotalBytes(file.size * -1);

                            let numberOfNormalFiles = component.state.numberOfNormalFiles;
                            let numberOfExtraFiles = component.state.numberOfExtraFiles;
                            let numberOfNoDeliveryFiles = component.state.numberOfNoDeliveryFiles;

                            switch (category) {
                                case 1:
                                    filesAdded--;
                                    numberOfNormalFiles--;
                                    break;
                                case 2:
                                    filesAdded--;
                                    numberOfExtraFiles--;
                                    break;
                                case 3:
                                    filesAdded--;
                                    numberOfNoDeliveryFiles--;
                                    break;
                            }

                            let fileArray = component.state.fileArray;
                            fileArray = fileArray.filter(
                                (storedFile: DropzoneFile) => storedFile.name !== file.name
                            );
                            component.setState({
                                fileArray: fileArray,
                                filesAdded: filesAdded,
                                numberOfNormalFiles: numberOfNormalFiles,
                                numberOfExtraFiles: numberOfExtraFiles,
                                numberOfNoDeliveryFiles: numberOfNoDeliveryFiles
                            } as IState);

                            if (component.props.updateFilesAdded) {
                                component.props.updateFilesAdded(filesAdded);
                            }
                        } else {
                            component.setState({ fileArray: [] } as IState);
                        }
                    }
                });

                this.on(
                    'uploadprogress',
                    (file: DropzoneFile, progress: number, bytesSent: number) => {
                        if (!file) return;
                        if (isNaN(bytesSent)) bytesSent = 0;
                        component.updateProgress(bytesSent, file.name, category);
                    }
                );

                this.on('success', (file: DropzoneFile, response: any) => {
                    if (file.previewElement.classList.contains('dz-error')) {
                        file.previewElement.classList.remove('dz-error');
                    }
                    
                    PhotographerUploadsApi.setImageInProgress({
                        WarmStorageReference: file.upload && file.upload.uuid,
                        FileName: file.upload && file.name,
                        OrderRowId: component.props.orderRowId,
                        Category: category,
                        UploadTime: new Date(file.lastModified).toISOString(),
                        ContentType: file.type,
                        ResumableTotalChunks: file.upload && file.upload.totalChunkCount
                    }, component.props.orderId).finally(() => {
                        switch (category) {
                            case 1:
                                component.setState((prevState) => ({
                                    numberOfNormalFilesProcessed:
                                        prevState.numberOfNormalFilesProcessed + 1
                                }));
                                break;
                            case 2:
                                component.setState((prevState) => ({
                                    numberOfExtraFilesProcessed:
                                        prevState.numberOfExtraFilesProcessed + 1
                                }));
                                break;
                            case 3:
                                component.setState((prevState) => ({
                                    numberOfNoDeliveryFilesProcessed:
                                        prevState.numberOfNoDeliveryFilesProcessed + 1
                                }));
                                break;
                            default:
                        }

                        if (component.checkIfAllFilesProcessed()) {
                            this.options.autoProcessQueue = false;
                            component.setState({
                                uploading: false,
                                uploadDone: true
                            } as IState);

                            if (component.props.setOrderRowIdAllFilesProcessed) {
                                component.props.setOrderRowIdAllFilesProcessed(
                                    component.props.orderRowId
                                );
                            }

                            if (component.props.resetAfterComplete) {
                                if (component._normalDropzone) {
                                    component._normalDropzone.removeAllFiles(true);
                                }

                                if (component._extraDropzone) {
                                    component._extraDropzone.removeAllFiles(true);
                                }

                                if (component._noDeliveryDropzone) {
                                    component._noDeliveryDropzone.removeAllFiles(true);
                                }

                                component.setState({
                                    uploadDone: false,
                                    totalBytes: 0
                                } as IState);
                            }
                            component.props.onUploadDone(true);
                        }
                    });
                });

                this.on('processing', () => {
                    this.options.autoProcessQueue = true;
                });

                this.on('error', (file: DropzoneFile, message: string | Error) => {
                    if (
                        typeof message === 'object' &&
                        message.message === 'Authorization has been denied for this request.'
                    ) {
                        let userManager = getUserManager();
                        userManager.signinSilent().then(() => {
                            let authHeader = new Api().getAuthData();
                            this.options.headers = {
                                Authorization: `Bearer ${authHeader.accessToken}`
                            };
                            this.processFile(file);
                        });
                    }
                    if (isTranslationOf(message, 'dropzone/invalid-filetype')) {
                        return;
                    }
                    component.updateProgress(0, file.name, category);
                    component.updateErrorForFile(file.name);
                    if (
                        !component.state.uploadError[file.name] ||
                        !(component.state.uploadError[file.name].nrOfErrors === 10 /* Number of Retry */)
                    ) {
                        setTimeout(() => {
                            this.processFile(file);
                        }, 20000); /* Retry after 20 sec */
                    } else {
                        notify.failure(`File ${file.name} failed to upload`);
                        component.props.updateTotalBytes(file.size * -1);
                        component.updateTotalBytes(file.size * -1);

                        switch (category) {
                            case 1:
                                component.setState((prevState) => ({
                                    numberOfNormalFilesProcessed:
                                        prevState.numberOfNormalFilesProcessed + 1
                                }));
                                break;
                            case 2:
                                component.setState((prevState) => ({
                                    numberOfExtraFilesProcessed:
                                        prevState.numberOfExtraFilesProcessed + 1
                                }));
                                break;
                            case 3:
                                component.setState((prevState) => ({
                                    numberOfNoDeliveryFilesProcessed:
                                        prevState.numberOfNoDeliveryFilesProcessed + 1
                                }));
                                break;
                            default:
                        }

                        if (component.checkIfAllFilesProcessed()) {
                            this.options.autoProcessQueue = false;
                            component.setState({
                                uploading: false,
                                uploadDone: true
                            } as IState);

                            if (component.props.setOrderRowIdAllFilesProcessed) {
                                component.props.setOrderRowIdAllFilesProcessed(
                                    component.props.orderRowId
                                );
                            }

                            if (component.props.resetAfterComplete) {
                                if (component._normalDropzone) {
                                    component._normalDropzone.removeAllFiles(true);
                                }

                                if (component._extraDropzone) {
                                    component._extraDropzone.removeAllFiles(true);
                                }

                                if (component._noDeliveryDropzone) {
                                    component._noDeliveryDropzone.removeAllFiles(true);
                                }

                                component.setState({
                                    uploadDone: false,
                                    totalBytes: 0
                                } as IState);
                            }
                            component.props.onUploadDone(true);
                        }
                    }
                });

                this.on('canceled', (file: DropzoneFile) => {
                    component.props.updateProgress(
                        0,
                        file.name,
                        component.props.orderRowId,
                        category
                    );
                    component.props.updateTotalBytes(file.size * -1);
                });
            }
        };

        return options;
    }

    removeFileFromFileDragState() {
        let fileDragState = this.state.fileDragState;

        switch (fileDragState.category) {
            case 1:
                this._normalDropzone.addFile(fileDragState.file);
                break;
            case 2:
                this._extraDropzone.addFile(fileDragState.file);
                break;
            case 3:
                this._noDeliveryDropzone.addFile(fileDragState.file);
                break;
        }
        fileDragState.category = 0;
        fileDragState.file = null;
        fileDragState.moving = false;
        this.props.toggleDragging(false);
        this.setState({ fileDragState: fileDragState } as IState);
    }

    updateTotalBytes(bytes: number) {
        let totalBytes = this.state.totalBytes;
        totalBytes += bytes;
        this.setState({ totalBytes: totalBytes } as IState);

        this.props.updateTotalBytes(bytes);
    }

    updateProgress(bytes: number, filename: string, category: number) {
        let progress = 0;
        let progressObject: any = this.state.progressObject;

        if (!progressObject[category]) {
            progressObject[category] = {};
        }
        progressObject[category][filename] = bytes;

        for (let category in progressObject) {
            for (let file in progressObject[category]) {
                progress += progressObject[category][file];
            }
        }

        let progressPercentage = parseInt(((progress / this.state.totalBytes) * 100).toFixed());

        this.setState({
            progressObject: progressObject,
            uploadProgress: Math.min(progressPercentage, 100)
        } as IState);
        this.props.updateProgress(bytes, filename, this.props.orderRowId, category);
    }

    updateErrorForFile(fileName: string) {
        let uploadError = this.state.uploadError;

        if (!uploadError[fileName]) {
            uploadError[fileName] = {};
            uploadError[fileName].nrOfErrors = 1;
        } else {
            uploadError[fileName].nrOfErrors++;
        }

        this.setState({ uploadError: uploadError } as IState);
    }

    render() {
        let numberOfDropzones =
            (this.props.hideNormalUploadBox !== true ? 1 : 0) +
            (this.props.hideExtraUploadBox !== true ? 1 : 0) +
            (this.props.hideNoDeliveryUploadBox !== true ? 1 : 0);

        let numberOfCols = 12 / numberOfDropzones;
        let colClassName = 'col-md-' + numberOfCols;

        const { apiUrl } = window.appConfig;
        const baseUrl = `${apiUrl}/views`;

        return (
            <div>
                <div className={`row ${this.state.fileDragState.moving ? 'dragging-row' : ''}`}>
                    {this.props.hideNormalUploadBox !== true && (
                        <div className={colClassName}>
                            <h4>
                                {Translate('photographer-uploads/order/row/dropzone-header/normal')}
                                <div className="pull-right">
                                    {Translate('photographer-uploads/order/row/files-added')}:{' '}
                                    {this.state.numberOfNormalFiles}
                                </div>
                            </h4>
                            <form
                                data-category="1"
                                action={`${baseUrl}/photographer-uploads/upload-chunk?orderId=${this.props.orderId}&orderRowId=${this.props.orderRowId}&category=1`}
                                id={`normal${this.props.orderRowId}`}
                                className={`dropzone dropzone-custom ${
                                    this.state.thumbnailTracking.category !== 1 ? 'waiting' : ''
                                }`}
                            ></form>
                        </div>
                    )}
                    {this.props.hideExtraUploadBox !== true && (
                        <div className={colClassName}>
                            <h4>
                                {this.props.customExtraUploadBoxTitle
                                    ? Translate(this.props.customExtraUploadBoxTitle)
                                    : Translate(
                                          'photographer-uploads/order/row/dropzone-header/extra'
                                      )}
                                <div className="pull-right">
                                    {Translate('photographer-uploads/order/row/files-added')}:{' '}
                                    {this.state.numberOfExtraFiles}
                                </div>
                            </h4>
                            <form
                                data-category="2"
                                action={`${baseUrl}/photographer-uploads/upload-chunk?orderId=${this.props.orderId}&orderRowId=${this.props.orderRowId}&category=2`}
                                id={`extra${this.props.orderRowId}`}
                                className={`dropzone dropzone-custom ${
                                    this.state.thumbnailTracking.category !== 2 ? 'waiting' : ''
                                }`}
                            ></form>
                        </div>
                    )}
                    {this.props.hideNoDeliveryUploadBox !== true && (
                        <div className={colClassName}>
                            <h4>
                                {Translate(
                                    'photographer-uploads/order/row/dropzone-header/no-delivery'
                                )}
                                <div className="pull-right">
                                    {Translate('photographer-uploads/order/row/files-added')}:{' '}
                                    {this.state.numberOfNoDeliveryFiles}
                                </div>
                            </h4>
                            <form
                                data-category="3"
                                action={`${baseUrl}/photographer-uploads/upload-chunk?orderId=${this.props.orderId}&orderRowId=${this.props.orderRowId}&category=3`}
                                id={`nodelivery${this.props.orderRowId}`}
                                className={`dropzone dropzone-custom ${
                                    this.state.thumbnailTracking.category !== 3 ? 'waiting' : ''
                                }`}
                            ></form>
                        </div>
                    )}
                </div>
                {this.state.uploading && (
                    <div className="dropzone-loading">
                        <ProgressBar
                            className="dropzone-progress"
                            active={this.state.uploadDone}
                            now={this.state.uploadProgress}
                            label={`${this.state.uploadProgress}%`}
                            max={100}
                            bsStyle="success"
                        />
                    </div>
                )}
            </div>
        );
    }
}

export default PhotographerUploadBoxes;

const listPhotoTemplate = `<div class="dz-preview dz-image-preview dz-preview-list grow">
<div class="dz-filename-list">
    <span class="dz-filename-text" data-dz-name=""></span>
    <div class="dz-remove-list">
        <i class="material-icons" data-dz-remove>delete</i>
    </div>
    <span class="dz-sizedata-list" data-dz-size=""></span>
</div>
<div class="dz-progress dz-progress-list"><span class="dz-upload dz-upload-list" data-dz-uploadprogress=""></span></div>
<div class="dz-success-mark">
    <svg width="54px" height="54px" viewBox="0 0 54 54" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" xmlnsSketch="http://www.bohemiancoding.com/sketch/ns">
        <title>Check</title>
        <defs></defs>
        <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" sketchType="MSPage">
            <path d="M23.5,31.8431458 L17.5852419,25.9283877 C16.0248253,24.3679711 13.4910294,24.366835 11.9289322,25.9289322 C10.3700136,27.4878508 10.3665912,30.0234455 11.9283877,31.5852419 L20.4147581,40.0716123 C20.5133999,40.1702541 20.6159315,40.2626649 20.7218615,40.3488435 C22.2835669,41.8725651 24.794234,41.8626202 26.3461564,40.3106978 L43.3106978,23.3461564 C44.8771021,21.7797521 44.8758057,19.2483887 43.3137085,17.6862915 C41.7547899,16.1273729 39.2176035,16.1255422 37.6538436,17.6893022 L23.5,31.8431458 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z"
                id="Oval-2" stroke-opacity="0.198794158" stroke="#747474" fill-opacity="0.816519475" fill="#FFFFFF" sketchType="MSShapeGroup"></path>
        </g>
    </svg>
</div>
<div class="dz-error-mark">
    <svg width="54px" height="54px" viewBox="0 0 54 54" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
        xmlnsSketch="http://www.bohemiancoding.com/sketch/ns">
        <title>Error</title>
        <defs></defs>
        <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" sketchType="MSPage">
            <g id="Check-+-Oval-2" sketchType="MSLayerGroup" stroke="#747474" stroke-opacity="0.198794158" fill="#FFFFFF" fill-opacity="0.816519475">
                <path d="M32.6568542,29 L38.3106978,23.3461564 C39.8771021,21.7797521 39.8758057,19.2483887 38.3137085,17.6862915 C36.7547899,16.1273729 34.2176035,16.1255422 32.6538436,17.6893022 L27,23.3431458 L21.3461564,17.6893022 C19.7823965,16.1255422 17.2452101,16.1273729 15.6862915,17.6862915 C14.1241943,19.2483887 14.1228979,21.7797521 15.6893022,23.3461564 L21.3431458,29 L15.6893022,34.6538436 C14.1228979,36.2202479 14.1241943,38.7516113 15.6862915,40.3137085 C17.2452101,41.8726271 19.7823965,41.8744578 21.3461564,40.3106978 L27,34.6568542 L32.6538436,40.3106978 C34.2176035,41.8744578 36.7547899,41.8726271 38.3137085,40.3137085 C39.8758057,38.7516113 39.8771021,36.2202479 38.3106978,34.6538436 L32.6568542,29 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z"
                    id="Oval-2" sketchType="MSShapeGroup"></path>
            </g>
        </g>
    </svg>
</div>
</div>`;
